import { useEffect, useRef } from 'react';
import { LearnAudioSession } from './learn_audio_session.ts';

// Polls the audio clock on requestAnimationFrame while `running` and hands each reading to
// `onTick` (issue #3473). Exercises derive every phase change from these readings rather than from
// timers, so a story that sets a fake session's clock and waits a frame sees the same transitions
// the real clock produces. The latest `onTick` is always the one called, so callers can pass a
// fresh closure every render without restarting the loop.
export function useAudioClock(audio: LearnAudioSession, running: boolean, onTick: (nowMs: number) => void) {
  const onTickRef = useRef(onTick);

  useEffect(() => {
    onTickRef.current = onTick;
  });

  useEffect(() => {
    if (!running) {
      return;
    }
    let frame = 0;
    const loop = () => {
      onTickRef.current(audio.nowMs());
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [audio, running]);
}

// The space bar as a tap on desktop while `enabled`: key repeat is ignored so holding the bar is
// one tap, and the default is prevented so the page does not scroll or activate a focused button.
// `onTap` receives the event's timeStamp, which is on the performance clock like performance.now().
export function useSpaceBarTap(enabled: boolean, onTap: (perfMs: number) => void) {
  const onTapRef = useRef(onTap);

  useEffect(() => {
    onTapRef.current = onTap;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== ' ' || event.repeat) {
        return;
      }
      event.preventDefault();
      onTapRef.current(event.timeStamp);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}

// The Enter key as "Next pattern" on desktop while `enabled`, so a session can be played from the
// keyboard alone (space bar to tap, Enter to move on). Key repeat is ignored.
export function useEnterKey(enabled: boolean, onPress: () => void) {
  const onPressRef = useRef(onPress);

  useEffect(() => {
    onPressRef.current = onPress;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.repeat) {
        return;
      }
      event.preventDefault();
      onPressRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
